/**
 * Pipebase Cloud in the SPA (spec 2026-10-30 section 6.5).
 *
 * `GET /api/auth/setup-state` says `cloud: true` on a hosted designer. It is
 * read before sign-in, so the login page knows too. In cloud mode the backend
 * refuses the routes behind the broker, pools, GitOps, custom domains, local
 * accounts and LDAP with `403 {"error":"not available on Pipebase Cloud"}`;
 * the SPA leaves those screens out instead of showing pages that fail:
 *
 * - nav items carry `cloudHidden: true` (shell/nav.ts) and are dropped;
 * - typing the URL of a hidden page shows `NotOnCloudPage`;
 * - forms leave out the fields a cloud designer refuses.
 *
 * Feature flags are not the mechanism: they answer 404, and an admin can
 * switch them back on.
 */
import { Link } from 'react-router-dom';
import { EmptyState } from '@pipebase/ui';
import { ApiError } from './api';
import { useAuth } from './auth/AuthContext';

export const NOT_ON_CLOUD = 'not available on Pipebase Cloud';

/** True on a Pipebase Cloud designer. False while setup-state is loading. */
export function useCloud(): boolean {
  return useAuth().setupState?.cloud === true;
}

/** The Pipebase Cloud console's address, on a cloud designer. */
export function useConsoleUrl(): string | null {
  const s = useAuth().setupState;
  return s?.cloud === true && typeof s.consoleUrl === 'string' ? s.consoleUrl : null;
}

/** Pipebase Cloud's free-plan limits; empty outside cloud mode. */
export function useCloudLimits(): { flows?: number; pipes?: number } {
  const s = useAuth().setupState;
  return s?.cloud === true ? (s.limits ?? {}) : {};
}

/** "3 of 25 flows", next to the button that creates one. */
export function PlanUsage({ used, limit, noun, testId }: { used: number; limit: number; noun: string; testId: string }) {
  const full = used >= limit;
  return (
    <span className="t-3" style={{ fontSize: 12.5, whiteSpace: 'nowrap' }} data-testid={testId}>
      {used} of {limit} {noun}
      {full ? ' · the free plan\u2019s limit' : ''}
    </span>
  );
}

/** Why a create button is off at the limit. */
export const planLimitHint = (limit: number, noun: string): string =>
  `The free plan holds ${limit} ${noun}. Delete one to make room.`;

/** The backend's refusal of a route or a field in cloud mode. */
export function isNotOnCloudError(e: unknown): boolean {
  return e instanceof ApiError && e.status === 403 && e.message === NOT_ON_CLOUD;
}

/** Pages a cloud designer does not have, by path prefix. The nav items for
 *  them are marked `cloudHidden` as well; this list also covers pages that
 *  have no nav item (pools, the GitOps settings, a flow's SLO page, the
 *  first-workspace wizard). */
const CLOUD_HIDDEN_PREFIXES = [
  '/mesh',
  '/schemas',
  '/event-designs',
  '/event-design/',
  '/event-catalog',
  '/marketplace',
  '/pools/',
  '/settings/gitops',
  '/onboarding/first-workspace',
] as const;

export function isCloudHiddenPath(pathname: string): boolean {
  if (/^\/edit\/[^/]+\/slo\/?$/.test(pathname)) return true;
  return CLOUD_HIDDEN_PREFIXES.some((p) =>
    p.endsWith('/') ? pathname.startsWith(p) : pathname === p || pathname.startsWith(`${p}/`),
  );
}

export function NotOnCloudPage() {
  return (
    <div className="page">
      <EmptyState
        data-testid="not-on-cloud"
        title="Not available on Pipebase Cloud"
        description="This part of Pipebase needs a self-hosted install: a broker, pools, GitOps or settings that belong to the platform."
        action={
          <Link to="/" className="btn btn--primary">
            Back to the dashboard
          </Link>
        }
      />
    </div>
  );
}
